import { useCallback, useMemo, useState, useEffect } from "react";
import { useWorker } from "./useWorker";
import Constants from "../utils/Constants";
import { useTranscriptionCache } from "../contexts/TranscriptionCacheContext";
import { v4 as uuidv4 } from "uuid";

interface ProgressItem {
    file: string;
    loaded: number;
    progress: number;
    total: number;
    name: string;
    status: string;
}

interface TranscriberUpdateData {
    data: [
        string,
        { chunks: { text: string; timestamp: [number, number | null] }[] },
    ];
    text: string;
}

interface TranscriberCompleteData {
    data: {
        text: string;
        chunks: { text: string; timestamp: [number, number | null] }[];
    };
}

export interface TranscriberData {
    id?: string;
    isBusy: boolean;
    text: string;
    title?: string;
    chunks: { text: string; timestamp: [number, number | null] }[];
}

export interface Transcriber {
    onInputChange: () => void;
    isBusy: boolean;
    isModelLoading: boolean;
    progressItems: ProgressItem[];
    start: (audioData: AudioBuffer | undefined) => void;
    output?: TranscriberData;
    model: string;
    setModel: (model: string) => void;
    multilingual: boolean;
    setMultilingual: (model: boolean) => void;
    quantized: boolean;
    setQuantized: (model: boolean) => void;
    subtask: string;
    setSubtask: (subtask: string) => void;
    language?: string;
    setLanguage: (language: string) => void;
}

export function useTranscriber(): Transcriber {
    const [transcript, setTranscript] = useState<TranscriberData | undefined>(undefined);
    const [transcriptionId, setTranscriptionId] = useState<string>("");
    const [isBusy, setIsBusy] = useState(false);
    const [isModelLoading, setIsModelLoading] = useState(false);
    const { getCachedTranscription, cacheTranscription } = useTranscriptionCache();

    const [progressItems, setProgressItems] = useState<ProgressItem[]>([]);

    // Try to restore cached transcription on mount
    useEffect(() => {
        if (transcriptionId) {
            getCachedTranscription(transcriptionId).then((cached) => {
                if (cached) {
                    setTranscript({
                        id: cached.id,
                        isBusy: false,
                        text: cached.transcriptionData.text,
                        chunks: cached.transcriptionData.chunks,
                    });
                }
            });
        }
    }, [transcriptionId, getCachedTranscription]);

    const webWorker = useWorker((event) => {
        const message = event.data;
        switch (message.status) {
            case "progress":
                setProgressItems((prev) =>
                    prev.map((item) => {
                        if (item.file === message.file) {
                            return { ...item, progress: message.progress };
                        }
                        return item;
                    }),
                );
                break;
            case "update":
                const updateMessage = message as TranscriberUpdateData;
                const updatedTranscript = {
                    id: transcriptionId,
                    isBusy: true,
                    text: updateMessage.data[0],
                    chunks: updateMessage.data[1].chunks,
                };
                setTranscript(updatedTranscript);
                break;
            case "complete":
                const completeMessage = message as TranscriberCompleteData;
                const completedTranscript: TranscriberData = {
                    id: transcriptionId,
                    isBusy: false,
                    text: completeMessage.data.text,
                    chunks: completeMessage.data.chunks,
                };

                // Generate title and cache the completed transcription
                if (transcriptionId) {
                    // Use an immediately invoked async function
                    (async () => {
                        try {
                            // Generate title using OpenAI via Netlify function
                            const response = await fetch('/.netlify/functions/openai', {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                    tool: 'generate_title',
                                    text: completeMessage.data.text
                                }),
                            });

                            if (!response.ok) {
                                throw new Error('Failed to generate title');
                            }

                            const titleResult = await response.json();
                            const title = titleResult.content[0].text;

                            // Cache transcription with title
                            cacheTranscription({
                                id: transcriptionId,
                                timestamp: Date.now(),
                                audioMetadata: {
                                    duration: completeMessage.data.chunks[completeMessage.data.chunks.length - 1]?.timestamp[0] || 0,
                                    sampleRate: 16000, // Default sample rate
                                    numberOfChannels: 1,
                                },
                                transcriptionData: {
                                    text: completeMessage.data.text,
                                    chunks: completeMessage.data.chunks,
                                    title: title,
                                },
                            });

                            // Update the completed transcript with the title
                            completedTranscript.title = title;
                            setTranscript(completedTranscript);
                        } catch (error) {
                            console.error('Failed to generate title:', error);
                            // Continue without title if generation fails
                            cacheTranscription({
                                id: transcriptionId,
                                timestamp: Date.now(),
                                audioMetadata: {
                                    duration: completeMessage.data.chunks[completeMessage.data.chunks.length - 1]?.timestamp[0] || 0,
                                    sampleRate: 16000,
                                    numberOfChannels: 1,
                                },
                                transcriptionData: {
                                    text: completeMessage.data.text,
                                    chunks: completeMessage.data.chunks,
                                },
                            });
                            setTranscript(completedTranscript);
                        }
                    })();
                } else {
                    setTranscript(completedTranscript);
                }
                setIsBusy(false);
                break;
            case "initiate":
                setIsModelLoading(true);
                setProgressItems((prev) => [...prev, message]);
                break;
            case "ready":
                setIsModelLoading(false);
                break;
            case "error":
                setIsBusy(false);
                throw new Error(message.data.message);
            case "done":
                setProgressItems((prev) =>
                    prev.filter((item) => item.file !== message.file),
                );
                break;
            default:
                break;
        }
    });

    const [model, setModel] = useState<string>(Constants.DEFAULT_MODEL);
    const [subtask, setSubtask] = useState<string>(Constants.DEFAULT_SUBTASK);
    const [quantized, setQuantized] = useState<boolean>(
        Constants.DEFAULT_QUANTIZED,
    );
    const [multilingual, setMultilingual] = useState<boolean>(
        Constants.DEFAULT_MULTILINGUAL,
    );
    const [language, setLanguage] = useState<string>(
        Constants.DEFAULT_LANGUAGE,
    );

    const onInputChange = useCallback(() => {
        setTranscript(undefined);
        setTranscriptionId("");
    }, []);

    const postRequest = useCallback(
        async (audioData: AudioBuffer | undefined) => {
            if (audioData) {
                const id = uuidv4();
                setTranscriptionId(id);
                setTranscript(undefined);
                setIsBusy(true);

                let audio;
                if (audioData.numberOfChannels === 2) {
                    const SCALING_FACTOR = Math.sqrt(2);
                    let left = audioData.getChannelData(0);
                    let right = audioData.getChannelData(1);
                    audio = new Float32Array(left.length);
                    for (let i = 0; i < audioData.length; ++i) {
                        audio[i] = SCALING_FACTOR * (left[i] + right[i]) / 2;
                    }
                } else {
                    audio = audioData.getChannelData(0);
                }

                webWorker.postMessage({
                    audio,
                    model,
                    multilingual,
                    quantized,
                    subtask: multilingual ? subtask : null,
                    language:
                        multilingual && language !== "auto" ? language : null,
                });
            }
        },
        [webWorker, model, multilingual, quantized, subtask, language],
    );

    const transcriber = useMemo(() => {
        return {
            onInputChange,
            isBusy,
            isModelLoading,
            progressItems,
            start: postRequest,
            output: transcript,
            model,
            setModel,
            multilingual,
            setMultilingual,
            quantized,
            setQuantized,
            subtask,
            setSubtask,
            language,
            setLanguage,
        };
    }, [
        isBusy,
        isModelLoading,
        progressItems,
        postRequest,
        transcript,
        model,
        multilingual,
        quantized,
        subtask,
        language,
    ]);

    return transcriber;
}
